/**
 * VerifiserBankID.jsx – offentlig landing for engangs-BankID-verifiseringslink.
 *
 * URL: /verifiser/:token
 * Tokenet utstedes av admin via /admin/seller-verifications/{id}/send-bankid-link.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Loader2, AlertCircle, Check, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function VerifiserBankID() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let alive = true;
    // Vis toast hvis vi nettopp kom tilbake fra Strex
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (status === "ok") toast.success("Identitet bekreftet via BankID!");
    else if (status === "aborted") toast.info("Du avbrøt BankID-flyten.");
    else if (status === "failed") toast.error("BankID-verifisering feilet. Prøv igjen.");

    fetch(`${BACKEND}/api/seller-verification/bankid/${token}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, body: d, status: r.status })))
      .then(({ ok, body, status }) => {
        if (!alive) return;
        if (!ok) {
          setError(body?.detail || (status === 410 ? "Linken er utløpt." : "Ugyldig link."));
        } else {
          setInfo(body);
        }
      })
      .catch(() => setError("Kunne ikke laste verifiseringsinfo."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const startBankID = async () => {
    setStarting(true);
    try {
      const r = await api.post(`/seller-verification/bankid/${token}/start`);
      if (r.data?.redirect_url) {
        window.location.href = r.data.redirect_url;
      } else if (r.data?.already_verified) {
        toast.success("Du er allerede verifisert.");
        window.location.reload();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "bankid_pending_docs") {
        toast.info(detail.message, { duration: 8000 });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ink-deep)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[var(--magenta)] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ink-deep)] flex items-center justify-center px-4">
        <div className="max-w-md w-full panel-card p-8 text-center" data-testid="bankid-error">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">Linken er ikke gyldig</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">{error}</p>
          <p className="font-manrope text-xs text-[var(--cream-faint)]">
            Be admin sende deg en ny verifiseringslink.
          </p>
          <a href="/" className="inline-flex items-center gap-2 mt-6 font-meta text-[11px] tracking-[0.2em] uppercase text-[var(--cream-soft)] hover:text-[var(--magenta)]">
            <ArrowLeft className="h-3 w-3" /> Til forsiden
          </a>
        </div>
      </div>
    );
  }

  const isVerified = info?.status === "verified";

  return (
    <div className="min-h-screen bg-[var(--ink-deep)] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full panel-card p-10" data-testid="bankid-landing">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-7 w-7 text-emerald-400" />
          <span className="font-meta text-[10px] tracking-[0.25em] uppercase text-[var(--cream-faint)]">
            Selger-verifisering
          </span>
        </div>
        <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)] mb-6 leading-tight">
          Hei {info?.display_name || info?.username}.
        </h1>

        {isVerified ? (
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-6 mb-6" data-testid="bankid-success">
            <Check className="h-8 w-8 text-emerald-400 mb-3" />
            <h2 className="font-fraunces italic text-xl text-[var(--cream)] mb-2">Identitet bekreftet!</h2>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-2">
              {info.verified_name ? <>Navn: <strong>{info.verified_name}</strong></> : "BankID-verifisering fullført."}
            </p>
            <p className="font-manrope text-xs text-[var(--cream-faint)]">
              Admin er varslet. Du får e-post når selger-statusen din er aktivert.
            </p>
          </div>
        ) : (
          <>
            <p className="font-manrope text-base text-[var(--cream-soft)] leading-relaxed mb-3">
              Vi har mottatt selger-søknaden din. Siste steg er en rask
              <strong className="text-[var(--cream)]"> BankID-bekreftelse</strong> – du logger
              inn med BankID via Strex og bekrefter at det er deg.
            </p>
            <p className="font-manrope text-sm text-[var(--cream-faint)] mb-8">
              Det tar under ett minutt. Vi lagrer bare at identiteten din er bekreftet
              – ikke fødselsnummer eller andre detaljer.
            </p>

            <button
              onClick={startBankID}
              disabled={starting}
              className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-4 disabled:opacity-50"
              data-testid="bankid-start-btn"
            >
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {starting ? "Starter BankID…" : "Start BankID-verifisering"}
            </button>
            <p className="font-manrope text-[11px] text-[var(--cream-faint)] mt-4 text-center">
              Krever norsk BankID. Strex er behandlingsansvarlig for selve verifiseringen.
            </p>
          </>
        )}

        <div className="mt-10 pt-6 border-t border-white/10">
          <a href="/" className="font-meta text-[10px] tracking-[0.2em] uppercase text-[var(--cream-faint)] hover:text-[var(--cream-soft)] inline-flex items-center gap-2">
            <ArrowLeft className="h-3 w-3" /> Til norskerotikk.no
          </a>
        </div>
      </div>
    </div>
  );
}
